import EventDispatcher from '../common/EventDispatcher';
import * as THREE from 'three';
import { Global } from '../common/global';
import nipplejs from 'nipplejs';
import { isMobile, isMobileOnly } from 'mobile-device-detect';
import gsap from 'gsap';
import { Lang } from '../common/lang';

export default class GameControl {
  constructor(scene) {
    this.scene = scene;

    // config
    this.minSwipeDistancePx = 20; // pixels required to count as a swipe

    // Joystick Instance
    this.joystickManager = null;
    this.eventsSentInitialActivation = false; // Prevents spamming 'hide_intro'

    // state
    this.isTouching = false;
    this.touchStartX = 0;
    this.lastX = 0;

    // NEW: Consolidated Movement Input State for Keyboard & Joystick
    this.input = {
      // For Keyboard: discrete movement
      isJoyStick: false,
      forward: false,
      forward2: false,
      left: false,
      right: false,

      // For Joystick: continuous movement vector
      // 'angle' is the direction (in degrees, 0=Right)
      // 'force' is the magnitude (speed) from 0.0 to 1.0
      angle: 0,
      force: 0,
      isJoystickActive: false, // Flag to indicate if joystick is being used
    };

    // event system
    this.events = null;

    // bound handlers so we can remove listeners later
    this._onTouchStartBound = this.onTouchStart.bind(this);
    this._onMoveBound = this.onMove.bind(this);
    this._onTouchEndBound = this.onTouchEnd.bind(this);
    this._onKeyDown = this.onKeyDown.bind(this);
    this._onKeyUp = this.onKeyUp.bind(this);

    this._onJoystickStart = this._onJoystickStart.bind(this);
    this._onJoystickMove = this._onJoystickMove.bind(this);
    this._onJoystickEnd = this._onJoystickEnd.bind(this);

    // Mouse/Key events (desktop)
    // document.addEventListener('mousedown', this._onTouchStartBound);
    // document.addEventListener('mousemove', this._onMoveBound);
    // document.addEventListener('mouseup', this._onTouchEndBound);
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('keyup', this._onKeyUp);
  }

  init() {
    this.joystickIntro = false;
    // EventDispatcher instance
    this.events = EventDispatcher.getObj();
    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents.bind(this));
  }
  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'start_game':
        this.input.forward = false;
        this.input.left = false;
        this.input.right = false;
        const isTouchAvailable =
          'ontouchstart' in window ||
          navigator.maxTouchPoints > 0 ||
          navigator.msMaxTouchPoints > 0;
        isTouchAvailable &&
          navigator.maxTouchPoints > 0 &&
          this.initJoystick('joystick-zone');
        break;
      case 'dispose':
        this.dispose();
        break;
      // case
    }
  }

  // --- Joystick Setup ---
  initJoystick(zoneId) {
    if (!nipplejs) {
      console.warn('nipplejs not found. Joystick controls will be disabled.');
      return;
    }
    const zone = document.getElementById(zoneId);

    if (!zone) {
      console.error(`Joystick zone #${zoneId} not found.`);
      return;
    }

    document.querySelector('#joystick-zone').classList.add('active');

    if (!this.joystickManager) {
      this.joystickManager = nipplejs.create({
        zone: zone,
        lockX: true,
        threshold: 1000,
        follow: false,
        mode: 'static',
        position: { left: '50%', top: '50%' },
        restOpacity: 0.3,
        color: 'white',
        size: Math.min(400, window.innerWidth * 0.6) * 1.8, // Match the CSS width/height
      });

      this.joystickManager.on('start', this._onJoystickStart);
      this.joystickManager.on('move', this._onJoystickMove);
      this.joystickManager.on('end', this._onJoystickEnd);

      this.scaleObj = { value: 1 };
      this.introTwn = gsap.delayedCall(0.5, () => {
        this.joystickIntro = true;
        document.querySelector('.joystick_intro').classList.add('active');
        setTimeout(() => {
          document.querySelector('.joystick_intro').classList.add('active2');
        }, 100);

        this.showAnimation();
      });
    }
  }
  hideIntro() {
    this.joystickIntro = false;
    this.introTwn1 && this.introTwn1.kill();
    this.introTwn2 && this.introTwn2.kill();
    this.introTwn3 && this.introTwn3.kill();

    document.querySelector('.joystick_intro').classList.remove('active2');
    setTimeout(() => {
      document.querySelector('.joystick_intro').classList.remove('active');
    }, 1000);
  }
  showAnimation() {
    this.frameDate = {
      value: 1,
    };
    document
      .querySelector('.joystick_intro .hand_icon')
      .classList.remove(`hand${this.lastVal}`);
    this.lastVal = 0;
    gsap.killTweensOf(this.frameDate);

    this.introTwn1 = gsap.to(this.scaleObj, {
      scale: 1, // Animates from 10 to 0
      duration: 0.2, // 10 seconds
      onStart: function () {
        document
          .querySelector('.joystick_intro .hand_icon')
          .classList.add(`hand1`);
        document.querySelector('.joystick_intro .label').innerHTML =
          Lang['introTap'][Global.lang];
      }.bind(this),
      onUpdate: function () {
        document.querySelector(
          '.joystick_intro .hand_icon'
        ).style.transform = `scale(${this.scaleObj.value})`;
      }.bind(this),
      ease: 'back.out',
    });

    this.introTwn2 = gsap.to(this.scaleObj, {
      value: 0.8, // Animates from 10 to 0
      duration: 0.25, // 10 seconds
      delay: 0.2,
      onUpdate: function () {
        document.querySelector(
          '.joystick_intro .hand_icon'
        ).style.transform = `scale(${this.scaleObj.value})`;
      }.bind(this),
      ease: 'back.out',
    });
    this.introTwn3 = gsap.to(this.frameDate, {
      value: 83, // Animates from 10 to 0
      duration: 2, // 10 seconds
      ease: 'none',
      repeat: 0,
      onStart: function () {
        document.querySelector('.joystick_intro .label').innerHTML =
          Lang['introSwipe'][Global.lang];
      }.bind(this),
      delay: 1,
      onUpdate: function () {
        if (this.lastVal != Math.floor(this.frameDate.value)) {
          document
            .querySelector('.joystick_intro .hand_icon')
            .classList.remove(`hand${this.lastVal}`);

          this.lastVal = Math.floor(this.frameDate.value);
          document
            .querySelector('.joystick_intro .hand_icon')
            .classList.add(`hand${this.lastVal}`);
        }
      }.bind(this),
      onComplete: this.showAnimation.bind(this),
    });
  }
  _onJoystickStart(evt, data) {
    if (!Global.isGameActive) return;

    // data.ui.front.style.transform = 'translate(1000px, 100px)';
    if (evt && evt.originalEvent) {
      // NippleJS often wraps the original touch/mouse event
      evt.originalEvent.stopPropagation();
      // Note: You must NOT use preventDefault here, as NippleJS already does it
    }
    console.log(data, 'data');

    // Set the joystick as active
    this.input.isJoystickActive = true;

    this.introTwn && this.introTwn.kill();
    if (this.joystickIntro) {
      this.hideIntro();
    }
    if (!this.eventsSentInitialActivation) {
      // Trigger the intro hide event only once
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'hide_intro',
          data: {},
        },
      });
      this.eventsSentInitialActivation = true;
    }

    // Call move to set initial position/force (it's 0 force at start)
    this._onJoystickMove(evt, data);
  }
  _onJoystickMove(evt, data) {
    if (!Global.isGameActive || !this.input.isJoystickActive) return;

    // data.angle.degree is the angle (0-360, 0 is right)
    // data.force is the magnitude (0.0 to 1.0)
    if (evt && evt.originalEvent) {
      // NippleJS often wraps the original touch/mouse event
      evt.originalEvent.stopPropagation();
      // Note: You must NOT use preventDefault here, as NippleJS already does it
    }
    let force = data.force || 0;
    let angle = data.angle ? data.angle.degree : this.input.angle;

    // --- Configuration for Mapping ---
    // Minimum force required to activate any input (to prevent drifting)
    const deadZone = 0.0;
    // Angle threshold for steering: defines the angle range considered "turning"
    // e.g., 60 degrees means +/- 30 degrees from the perfect forward/backward axes.
    const turnAngleThreshold = 0;

    // 1. Update the continuous state
    this.input.force = Math.min(force, 1.0);
    this.input.angle = angle;
    this.input.isJoyStick = true;

    // 2. Reset discrete keyboard states
    this.input.forward = true;
    this.input.forward2 = true;
    this.input.left = false;
    this.input.right = false;

    if (this.input.force >= deadZone) {
      // Map nipplejs angle (0=Right, 90=Up, 180=Left, 270=Down)
      // to the 4 quadrants: Forward, Left, Right, Backward (if applicable)

      // --- Forward Check (Primarily between 45 and 135 degrees) ---
      // This covers the upper 90-degree arc of the stick
      if (angle >= 45 + 35 && angle <= 135 - 35) {
        this.input.forward = true;
      }
      // NOTE: You can add `Backward` logic if needed (225 to 315 degrees)

      // --- Steering Check (Left/Right) ---
      // Use the angle to determine steering. We'll use a simple threshold
      // relative to the perfect forward line (90 degrees).

      // Calculate the difference from the perfect forward angle (90 degrees)
      const diffFromForward = Math.abs(angle - 90);

      // If the difference is greater than the threshold, we are turning.
      if (diffFromForward > turnAngleThreshold / 2) {
        let normalized = Math.min(90, diffFromForward);
        normalized = (normalized * Math.PI) / 180;
        normalized = Math.sin(normalized);
        // console.log(this.input.force);
        Global.rotateFact =
          this.input.force < 0.2
            ? this.input.force * 0.7
            : this.input.force > 0.75
            ? this.input.force * 1.1
            : this.input.force; //normalized * Math.min(1, this.input.force);

        if (!this.input.forward || true) {
          if (angle > 90 && angle < 270) {
            this.input.left = true;
            // this.input.forward = false;
          } else {
            this.input.right = true;
            // this.input.forward = false;
          }
        }
      }

      // IMPORTANT ADJUSTMENT for Forward Movement:
      // In many games, moving the stick *at all* makes you go forward.
      // If you want the character to move forward whenever the joystick is active,
      // uncomment the line below:
      // this.input.forward = true;
    }

    // 3. Dispatch the updated state
    this.dispatchInputUpdate();
  }
  // _onJoystickMove(evt, data) {
  //   if (!Global.isGameActive || !this.input.isJoystickActive) return;

  //   // data.angle.degree is the angle (0-360, 0 is right)
  //   // data.force is the magnitude (0.0 to 1.0)

  //   let force = data.force || 0; // ensure it's at least 0
  //   let angle = data.angle ? data.angle.degree : this.input.angle; // keep last angle if no data

  //   // Clamp force between 0 and 1
  //   this.input.force = Math.min(force, 1.0);
  //   this.input.angle = angle;

  //   this.dispatchInputUpdate();
  // }

  _onJoystickEnd() {
    if (!Global.isGameActive) return;

    // Reset joystick state
    this.input.angle = 0;
    this.input.force = 0;
    this.input.left = false;
    this.input.right = false;
    this.input.forward = false;
    this.input.isJoystickActive = false; // Joystick is no longer active

    this.dispatchInputUpdate();
  }
  // --- End Joystick Setup ---

  dispatchInputUpdate() {
    if (!Global.isGameActive) return;

    this.events.dispatchEvent({
      type: 'PLAYER_EVENTS',
      message: {
        event_type: 'PLAYER_INPUT_UPDATE',
        data: {
          inputState: { ...this.input }, // Send a copy of the current state
        },
      },
    });
  }

  onKeyDown(event) {
    if (!Global.isGameActive) return;

    // Ignore keyboard input if joystick is currently active (mobile vs desktop)
    if (this.input.isJoystickActive) return;

    let stateChanged = false;

    switch (event.key.toLowerCase()) {
      case 'arrowup':
      case 'w':
        if (!this.input.forward) {
          this.input.forward = true;
          stateChanged = true;
        }
        break;
      case 'arrowleft':
      case 'a':
        if (!this.input.left) {
          this.input.left = true;
          stateChanged = true;
        }
        break;
      case 'arrowright':
      case 'd':
        if (!this.input.right) {
          this.input.right = true;
          stateChanged = true;
        }
        break;

      default:
        return;
    }

    // Only dispatch if the state actually changed
    if (stateChanged) {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'hide_intro',
          data: {},
        },
      });
      this.dispatchInputUpdate();
    }
  }

  onKeyUp(event) {
    if (!Global.isGameActive) return;

    // Ignore keyboard input if joystick is currently active
    if (this.input.isJoystickActive) return;

    let stateChanged = false;

    switch (event.key.toLowerCase()) {
      case 'arrowup':
      case 'w':
        if (this.input.forward) {
          this.input.forward = false;
          stateChanged = true;
        }
        break;
      case 'arrowleft':
      case 'a':
        if (this.input.left) {
          this.input.left = false;
          stateChanged = true;
        }
        break;
      case 'arrowright':
      case 'd':
        if (this.input.right) {
          this.input.right = false;
          stateChanged = true;
        }
        break;
      default:
        return;
    }

    if (stateChanged) {
      this.dispatchInputUpdate();
    }
  }

  // NOTE: Original touch/swipe logic is left in but modified.
  // For a pure mobile experience, you would likely remove screen-wide swipe
  // when using a dedicated joystick.

  onTouchStart(event) {
    if (!Global.isGameActive) return;

    // If the joystick is active, ignore other touch events to prevent conflicts
    if (this.input.isJoystickActive) return;

    const isTouch = event.type.startsWith('touch');
    const clientX = isTouch ? event.touches[0].clientX : event.clientX;

    this.isTouching = true;
    this.touchStartX = clientX;
    this.lastX = clientX;

    // Removed the temporary 'this.input.forward = true' from original mobile touch logic
  }

  onMove(event) {
    if (!Global.isGameActive) return;
    if (!this.isTouching || this.input.isJoystickActive) return; // Ignore if joystick is active

    const isTouch = event.type.startsWith('touch');
    const clientX = isTouch ? event.touches[0].clientX : event.clientX;

    this.lastX = clientX;
  }

  onTouchEnd(event) {
    if (!Global.isGameActive) return;
    if (!this.isTouching || this.input.isJoystickActive) return; // Ignore if joystick is active

    // compute overall delta
    const deltaX = this.lastX - this.touchStartX;

    // determine swipe direction with a threshold
    let direction = null;
    if (deltaX > this.minSwipeDistancePx) direction = 'Right';
    else if (deltaX < -this.minSwipeDistancePx) direction = 'Left';

    // --- Swipe Handling ---
    if (direction === 'Left' || direction === 'Right') {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'hide_intro',
          data: {},
        },
      });

      // Send a momentary event for the swipe action (for turning)
      this.events.dispatchEvent({
        type: 'PLAYER_EVENTS',
        message: {
          event_type: 'on_swipe', // Player will interpret this as a momentary turn
          data: {
            direction,
          },
        },
      });
    }

    // reset state
    this.isTouching = false;
    this.touchStartX = 0;
    this.lastX = 0;
  }

  // Clean up listeners
  dispose() {
    // document.removeEventListener('mousedown', this._onTouchStartBound);
    // document.removeEventListener('mousemove', this._onMoveBound);
    // document.removeEventListener('mouseup', this._onTouchEndBound);

    if (this.joystickIntro) {
      this.hideIntro();
    }

    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('keyup', this._onKeyUp);

    // Dispose of the joystick instance if it exists
    if (this.joystickManager) {
      this.joystickManager.off('start', this._onJoystickStart);
      this.joystickManager.off('move', this._onJoystickMove);
      this.joystickManager.off('end', this._onJoystickEnd);
      this.joystickManager.destroy();
    }
  }
}
